import type { SidebarNavGroupId } from "@/config/sidebar-nav";

/**
 * localStorage key prefix for the signed-in user's last active product,
 * scoped per user (not per business — a user's product preference follows
 * them, mirroring `graftus_activeTeamId_` in TeamScopeContext which is scoped
 * per business instead).
 */
const STORAGE_PREFIX = "graftus_activeProduct_";

/** Where the sidebar / router send you when you switch into a product, or land on `/` with it remembered. */
export const PRODUCT_LANDING_PATH: Record<SidebarNavGroupId, string> = {
  coach: "/",
  recruit: "/recruit/candidates",
  leads: "/leads",
};

/**
 * The product that owns `pathname`, by URL prefix — the constraint from
 * GRA-78 that a page belongs to exactly one product, never a fallback bucket.
 * `/` is Coach's own landing page (Today/Dashboard). Company-wide pages
 * (`/settings/*`, `/apps`, `/profile`, `/more`, etc.) own no product and
 * resolve to `undefined`; callers fall back to the last remembered product.
 */
export function getActiveProductForPathname(
  pathname: string,
): SidebarNavGroupId | undefined {
  if (pathname === "/") return "coach";
  if (pathname === "/coach" || pathname.startsWith("/coach/")) return "coach";
  if (pathname === "/recruit" || pathname.startsWith("/recruit/"))
    return "recruit";
  if (pathname === "/leads" || pathname.startsWith("/leads/")) return "leads";
  return undefined;
}

/** The signed-in user's last chosen product, or null if never set / not available yet. */
export function getStoredActiveProduct(
  uid: string,
): SidebarNavGroupId | null {
  if (!uid) return null;
  const stored = localStorage.getItem(STORAGE_PREFIX + uid);
  if (stored === "coach" || stored === "recruit" || stored === "leads") {
    return stored;
  }
  return null;
}

export function setStoredActiveProduct(
  uid: string,
  product: SidebarNavGroupId,
): void {
  if (!uid) return;
  localStorage.setItem(STORAGE_PREFIX + uid, product);
}
